import React, { useState, useEffect, useRef } from 'react';
import * as XLSX from 'xlsx';
import { FileDown, ArrowLeft, Shield, TrendingUp, History, Lock, Filter, X, Activity } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import LogoLoader from '../components/LogoLoader';

const ALLOWED_EMAIL = 'alameengoldandsilver@gmail.com';

function MultiSelect({ options, selected, onChange, placeholder = 'All' }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);
  const toggle = (val) => {
    onChange(selected.includes(val) ? selected.filter(v => v !== val) : [...selected, val]);
  };
  const displayLabel = selected.length === 0
    ? placeholder
    : selected.length === 1
      ? (options.find(o => String(o.value) === String(selected[0]))?.label || selected[0])
      : `${selected.length} selected`;
  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px',
          padding: '9px 14px', borderRadius: '10px', border: '1.5px solid #e2e8f0',
          fontSize: '14px', color: selected.length ? '#0f172a' : '#94a3b8',
          background: selected.length ? '#fef9ec' : '#f8fafc',
          cursor: 'pointer', outline: 'none', minWidth: '160px',
          borderColor: selected.length ? '#C9A84C' : '#e2e8f0',
          fontWeight: selected.length ? '600' : '400',
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{displayLabel}</span>
        <span style={{ fontSize: '10px', color: '#94a3b8', flexShrink: 0 }}>{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 200,
          background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: '10px',
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)', minWidth: '100%', maxHeight: '240px',
          overflowY: 'auto', padding: '6px 0',
        }}>
          {options.map(opt => {
            const checked = selected.includes(String(opt.value));
            return (
              <label key={opt.value} style={{
                display: 'flex', alignItems: 'center', gap: '10px',
                padding: '8px 14px', cursor: 'pointer', fontSize: '13px',
                color: '#334155', background: checked ? '#fef9ec' : 'transparent',
                fontWeight: checked ? '600' : '400',
              }}>
                <input
                  type="checkbox" checked={checked}
                  onChange={() => toggle(String(opt.value))}
                  style={{ accentColor: '#C9A84C', width: '15px', height: '15px', cursor: 'pointer' }}
                />
                {opt.label}
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}

const s = {
  page: { height: '100vh', background: '#faf7f0', fontFamily: "'Segoe UI', sans-serif", display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  navbar: {
    background: 'linear-gradient(135deg, #0a0a0a, #1a1a1a)',
    padding: '0 20px', height: '64px',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    boxShadow: '0 2px 20px rgba(0,0,0,0.3)',
    flexShrink: 0,
  },
  navBrand: { display: 'flex', alignItems: 'center', gap: '10px' },
  navCoin: {
    width: '42px', height: '42px',
    borderRadius: '50%', overflow: 'hidden',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0,
  },
  navTitle: { color: '#fff', fontSize: '18px', fontWeight: '800', margin: 0 },
  backBtn: {
    display: 'flex', alignItems: 'center', gap: '6px',
    background: 'rgba(255,255,255,0.1)', color: '#cbd5e1',
    border: '1px solid rgba(255,255,255,0.15)',
    padding: '8px 14px', borderRadius: '8px',
    fontSize: '13px', fontWeight: '600', cursor: 'pointer',
  },
  content: { padding: '32px 24px' },
  headerCard: {
    background: '#fff', borderRadius: '16px', padding: '24px 28px',
    marginBottom: '24px', boxShadow: '0 1px 8px rgba(0,0,0,0.07)',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  },
  headerLeft: { display: 'flex', alignItems: 'center', gap: '14px' },
  shieldBox: {
    width: '48px', height: '48px',
    background: 'linear-gradient(135deg, #C9A84C, #9A7A2C)',
    borderRadius: '12px', display: 'flex', alignItems: 'center',
    justifyContent: 'center', color: '#fff',
  },
  headerTitle: { fontSize: '20px', fontWeight: '800', color: '#0f172a', margin: 0 },
  headerSub: { fontSize: '13px', color: '#64748b', margin: '3px 0 0' },
  exportBtn: {
    display: 'flex', alignItems: 'center', gap: '8px',
    background: 'linear-gradient(135deg, #C9A84C, #9A7A2C)',
    color: '#fff', border: 'none', padding: '12px 22px',
    borderRadius: '10px', fontSize: '14px', fontWeight: '700',
    cursor: 'pointer', boxShadow: '0 4px 12px rgba(201,168,76,0.3)',
  },
  tableCard: { background: '#fff', borderRadius: '16px', boxShadow: '0 1px 8px rgba(0,0,0,0.07)', overflow: 'hidden' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { padding: '11px 14px', textAlign: 'left', fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', background: '#f8fafc', letterSpacing: '0.5px', position: 'sticky', top: 0, zIndex: 1, whiteSpace: 'nowrap' },
  td: { padding: '12px 14px', borderBottom: '1px solid #f1f5f9', fontSize: '13px', color: '#334155', whiteSpace: 'nowrap' },
  badge: { display: 'inline-block', background: '#fef3c7', color: '#D97706', borderRadius: '20px', padding: '2px 10px', fontSize: '12px', fontWeight: '700' },
  amtBadge: { display: 'inline-block', background: '#ecfdf5', color: '#10b981', borderRadius: '20px', padding: '2px 10px', fontSize: '12px', fontWeight: '700' },
};

export default function AdminPortal() {
  const [data, setData] = useState([]);
  const [dataLoading, setDataLoading] = useState(true);
  const [allowed, setAllowed] = useState(null); // null=checking, true=ok, false=denied
  const [selectedMonths, setSelectedMonths] = useState([]);
  const [selectedYears, setSelectedYears] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const [txLogs, setTxLogs] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [deletedUsers, setDeletedUsers] = useState([]);
  const [activeTab, setActiveTab] = useState('members'); // 'members' | 'gold' | 'silver' | 'money' | 'closed'
  const [showFilters, setShowFilters] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Guard: only allow the specific admin email
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user?.email?.toLowerCase() !== ALLOWED_EMAIL) {
        setAllowed(false);
        setDataLoading(false);
      } else {
        setAllowed(true);
        loadData();
      }
    });
  }, [navigate]);

  const loadData = async () => {
    setDataLoading(true);
    const [{ data: rows }, { data: usrs }, { data: txData }, { data: delUsrs }] = await Promise.all([
      supabase.from('amounts').select('*, users(name, saving_type)').order('created_at', { ascending: false }),
      supabase.from('users').select('id, name, saving_type, phone, created_at, added_by'),
      supabase.from('transaction_logs').select('*').in('event_type', ['amount_deleted', 'user_deleted']).order('created_at', { ascending: false }),
      supabase.from('deleted_users').select('*').order('deleted_at', { ascending: false }),
    ]);
    setData(rows || []);
    setAllUsers(usrs || []);
    setTxLogs(txData || []);
    setDeletedUsers(delUsrs || []);
    setDataLoading(false);
  };

  const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  const currentYear = new Date().getFullYear();
  const availableYears = [...new Set([currentYear, ...data.map(d => new Date(d.date).getFullYear())])].sort((a, b) => b - a);
  const isAnyFilter = !!(selectedMonths.length || selectedYears.length || startDate || endDate);
  const filteredData = data.filter(d => {
    const date = new Date(d.date);
    if (selectedMonths.length && !selectedMonths.includes(String(date.getMonth() + 1))) return false;
    if (selectedYears.length && !selectedYears.includes(String(date.getFullYear()))) return false;
    if (startDate && date < new Date(startDate + 'T00:00:00')) return false;
    if (endDate && date > new Date(endDate + 'T23:59:59')) return false;
    return true;
  });

  // ── Deleted transactions (transaction_logs only) ─────────────────────────
  // From individual amount_deleted events
  const txAmountDeletedRows = txLogs
    .filter(l => l.event_type === 'amount_deleted')
    .map(log => ({
      _key: `tx-${log.id}`,
      amount_id: log.amount_id,
      user_id: log.user_id || log.new_data?.user_id || null,
      saving_type: log.saving_type || '',
      member_name: log.member_name || '—',
      amount: Number(log.amount || log.old_data?.amount || 0),
      gram: Number(log.gram || log.old_data?.gram || 0),
      date: log.old_data?.date || log.new_data?.date || '',
      created_at: log.old_data?.created_at || log.new_data?.created_at || log.created_at || null,
      note: log.note || log.old_data?.note || log.new_data?.note || '—',
      added_by: log.old_data?.added_by || log.new_data?.added_by || '—',
      deleted_by: log.performed_by || '—',
      deleted_at: log.old_data?.deleted_at || log.created_at,
    }));

  // From user_deleted events (historical: member deleted before per-amount logging was enabled)
  // Only include if no amount_deleted entries exist for that user_id
  const amountDeletedUserIds = new Set(txAmountDeletedRows.map(r => String(r.user_id)).filter(Boolean));
  const txUserDeletedRows = txLogs
    .filter(l => l.event_type === 'user_deleted' && !amountDeletedUserIds.has(String(l.user_id)))
    .map(log => ({
      _key: `ud-${log.id}`,
      amount_id: null,
      user_id: log.user_id || null,
      saving_type: log.saving_type || '',
      member_name: log.member_name || '—',
      amount: Number(log.amount || log.old_data?.live_balance || 0),
      gram: Number(log.gram || log.old_data?.total_gram || 0),
      date: log.old_data?.deleted_at || log.created_at || '',
      created_at: log.created_at || null,
      note: '—',
      added_by: '—',
      deleted_by: log.performed_by || '—',
      deleted_at: log.old_data?.deleted_at || log.created_at,
    }));

  const parsedDeletedRows = [...txAmountDeletedRows, ...txUserDeletedRows]
    .sort((a, b) => new Date(b.deleted_at || 0) - new Date(a.deleted_at || 0));

  // ── Deleted users (transaction_logs + legacy deleted_users) ───────────────
  const txDeletedUsers = txLogs
    .filter(l => l.event_type === 'user_deleted')
    .map(log => ({
      original_id: log.old_data?.original_id || log.new_data?.id,
      name: log.member_name || log.new_data?.name || '—',
      saving_type: log.saving_type || log.new_data?.saving_type || '',
      phone: log.phone || log.new_data?.phone || '—',
      deleted_at: log.old_data?.deleted_at || log.created_at,
      deleted_by: log.performed_by || '—',
    }));

  const deletedUserOrigIds = new Set(deletedUsers.map(d => String(d.original_id)).filter(Boolean));
  const extraTxDeletedUsers = txDeletedUsers.filter(u => u.original_id && !deletedUserOrigIds.has(String(u.original_id)));
  const allDeletedUsers = [...deletedUsers, ...extraTxDeletedUsers];

  // ── Date filter helper ─────────────────────────────────────────────────────
  const passesDateFilter = (dateStr, isTs = false) => {
    if (!dateStr) return false;
    const dt = isTs ? new Date(dateStr) : new Date(dateStr + 'T00:00:00');
    if (selectedMonths.length && !selectedMonths.includes(String(dt.getMonth() + 1))) return false;
    if (selectedYears.length && !selectedYears.includes(String(dt.getFullYear()))) return false;
    if (startDate && dt < new Date(startDate + 'T00:00:00')) return false;
    if (endDate && dt > new Date(endDate + 'T23:59:59')) return false;
    return true;
  };

  // ── Filtered rows ─────────────────────────────────────────────────────────
  const isDateFilter = !!(selectedMonths.length || selectedYears.length || startDate || endDate);
  const filteredJoined  = isDateFilter ? allUsers.filter(u => passesDateFilter(u.created_at, true)) : allUsers;
  const filteredRemoved = isDateFilter ? allDeletedUsers.filter(u => passesDateFilter(u.deleted_at, true)) : allDeletedUsers;
  const filteredClosed = isDateFilter
    ? parsedDeletedRows.filter(d => passesDateFilter(d.date) || passesDateFilter(d.deleted_at, true))
    : parsedDeletedRows;

  // ── Overall totals (never filtered) ───────────────────────────────────────
  const overallTotalAmount = data.reduce((s, d) => s + Number(d.amount || 0), 0);
  const overallGoldGram    = data.filter(d => d.users?.saving_type === 'gold').reduce((s, d) => s + Number(d.gram || 0), 0);
  const overallSilverGram  = data.filter(d => d.users?.saving_type === 'silver').reduce((s, d) => s + Number(d.gram || 0), 0);
  const totalActiveMembers = allUsers.length;

  // ── Per-type filtered splits ──────────────────────────────────────────────
  const goldCollected   = filteredData.filter(d => d.users?.saving_type === 'gold');
  const silverCollected = filteredData.filter(d => d.users?.saving_type === 'silver');
  const moneyCollected  = filteredData.filter(d => d.users?.saving_type === 'money');
  const goldClosed      = filteredClosed.filter(d => d.saving_type === 'gold');
  const silverClosed    = filteredClosed.filter(d => d.saving_type === 'silver');
  const moneyClosed     = filteredClosed.filter(d => d.saving_type === 'money');

  // ── Section stats ─────────────────────────────────────────────────────────
  const goldCollectedAmt    = goldCollected.reduce((s, d) => s + Number(d.amount || 0), 0);
  const goldCollectedGram   = goldCollected.reduce((s, d) => s + Number(d.gram || 0), 0);
  const goldClosedAmt       = goldClosed.reduce((s, d) => s + Number(d.amount || 0), 0);
  const goldClosedGram      = goldClosed.reduce((s, d) => s + Number(d.gram || 0), 0);
  const silverCollectedAmt  = silverCollected.reduce((s, d) => s + Number(d.amount || 0), 0);
  const silverCollectedGram = silverCollected.reduce((s, d) => s + Number(d.gram || 0), 0);
  const silverClosedAmt     = silverClosed.reduce((s, d) => s + Number(d.amount || 0), 0);
  const silverClosedGram    = silverClosed.reduce((s, d) => s + Number(d.gram || 0), 0);
  const moneyCollectedAmt   = moneyCollected.reduce((s, d) => s + Number(d.amount || 0), 0);
  const moneyClosedAmt      = moneyClosed.reduce((s, d) => s + Number(d.amount || 0), 0);

  // ── Members tab combined data ─────────────────────────────────────────────
  const membersTabRows = [
    ...filteredJoined.map(u => ({
      _type: 'joined', id: u.id, name: u.name, saving_type: u.saving_type, phone: u.phone, date: u.created_at, extra: u.added_by,
    })),
    ...filteredRemoved.map(u => ({
      _type: 'removed', id: u.original_id, name: u.name, saving_type: u.saving_type, phone: u.phone, date: u.deleted_at, extra: u.deleted_by,
    })),
  ].sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));

  // ── Table data for active tab ─────────────────────────────────────────────
  const tableData = activeTab === 'members' ? membersTabRows
    : activeTab === 'gold'   ? goldCollected
    : activeTab === 'silver' ? silverCollected
    : activeTab === 'money'  ? moneyCollected
    : filteredClosed;

  const downloadExcel = () => {
    const rows = tableData.map(d => {
      if (activeTab === 'members') {
        return {
          Status: d._type === 'joined' ? 'Joined' : 'Removed',
          'Customer ID': d.id || '—',
          Name: d.name || '—',
          Type: d.saving_type || '—',
          Phone: d.phone || '—',
          Date: d.date ? new Date(d.date).toLocaleDateString('en-IN') : '—',
        };
      }
      if (activeTab === 'closed') {
        return {
          Member: d.member_name || '—',
          'Customer ID': d.user_id || '—',
          Type: d.saving_type || '—',
          Gram: d.gram ? Number(d.gram).toFixed(3) : '',
          Amount: d.amount,
          'Transaction Date': d.date,
          Note: d.note || '',
          'Deleted By': d.deleted_by || '—',
          'Deleted At': d.deleted_at ? new Date(d.deleted_at).toLocaleString('en-IN') : '',
        };
      }
      return {
        'Customer ID': d.user_id || '—',
        Member: d.users?.name || '—',
        Gram: d.gram ? Number(d.gram).toFixed(3) : '',
        Amount: d.amount,
        Date: d.date,
        Note: d.note || '',
        'Added By': d.added_by || '—',
      };
    });
    const ws = XLSX.utils.json_to_sheet(rows);
    // Auto-fit column widths based on data
    if (rows.length > 0) {
      const headers = Object.keys(rows[0]);
      ws['!cols'] = headers.map(h => ({
        wch: Math.max(h.length, ...rows.map(r => String(r[h] ?? '').length)) + 2,
      }));
    }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, activeTab.charAt(0).toUpperCase() + activeTab.slice(1));
    XLSX.writeFile(wb, `GoldSaving_${activeTab}_Report.xlsx`);
  };

  const clearFilters = () => {
    setSelectedMonths([]);
    setSelectedYears([]); setStartDate(''); setEndDate('');
  };
 

  if (allowed === null || dataLoading) return <LogoLoader />;

  if (allowed === false) return (
    <div style={{ minHeight: '100vh', background: '#faf7f0', fontFamily: "'Segoe UI', sans-serif", display: 'flex', flexDirection: 'column' }}>
      <nav style={s.navbar}>
        <div style={s.navBrand}>
          <div style={{ ...s.navCoin, cursor: 'pointer' }} onClick={() => window.location.reload()}>
            <img src="/logo.jpg" alt="Al Ameen" style={{ width: '42px', height: '42px', objectFit: 'cover', borderRadius: '50%' }} />
          </div>
          <p style={s.navTitle}>Al Ameen Gold &amp; Silver</p>
        </div>
        <button onClick={() => navigate('/dashboard')} style={s.backBtn}>
          <ArrowLeft size={15} /> Back
        </button>
      </nav>
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
        <div style={{
          background: '#fff', borderRadius: '20px', padding: '48px 40px',
          maxWidth: '420px', width: '100%', textAlign: 'center',
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
        }}>
          <div style={{
            width: '72px', height: '72px', borderRadius: '50%',
            background: 'rgba(239,68,68,0.1)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px',
          }}>
            <Lock size={34} color="#ef4444" />
          </div>
          <h2 style={{ margin: '0 0 10px', fontSize: '22px', fontWeight: '800', color: '#0f172a' }}>Access Denied</h2>
          <p style={{ margin: '0 0 6px', fontSize: '15px', color: '#64748b' }}>You don't have permission to view the Admin Portal.</p>
          <p style={{ margin: '0 0 28px', fontSize: '13px', color: '#94a3b8' }}>This page is restricted to authorised administrators only.</p>
          <button onClick={() => navigate('/dashboard')} style={{
            display: 'inline-flex', alignItems: 'center', gap: '8px',
            background: 'linear-gradient(135deg, #0a0a0a, #1a1a1a)',
            color: '#fff', border: 'none', padding: '12px 24px',
            borderRadius: '10px', fontSize: '14px', fontWeight: '700', cursor: 'pointer',
          }}>
            <ArrowLeft size={16} /> Go to Dashboard
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div style={s.page}>
      <nav style={s.navbar} className="gs-navbar">
        <div style={s.navBrand}>
          <div style={{ ...s.navCoin, cursor: 'pointer' }} className="gs-nav-coin" onClick={() => window.location.reload()}>
            <img src="/logo.jpg" alt="Al Ameen" style={{ width: '42px', height: '42px', objectFit: 'cover', borderRadius: '50%', display: 'block' }} />
          </div>
          <p style={s.navTitle} className="gs-nav-title">Al Ameen Gold & Silver</p>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }} className="gs-admin-nav">
          <button onClick={() => navigate('/metal-rates')} style={s.backBtn} className="gs-nav-btn">
            <TrendingUp size={15} /><span className="gs-btn-label">&nbsp;Rates</span>
          </button>
          <button onClick={() => navigate('/delete-history')} style={s.backBtn} className="gs-nav-btn">
            <History size={15} /><span className="gs-btn-label">&nbsp;History</span>
          </button>
          <button onClick={() => navigate('/admin-activity')} style={s.backBtn} className="gs-nav-btn">
            <Activity size={15} /><span className="gs-btn-label">&nbsp;Activity</span>
          </button>
          <button onClick={() => navigate('/dashboard')} style={s.backBtn} className="gs-nav-btn">
            <ArrowLeft size={15} /><span className="gs-btn-label">&nbsp;Dashboard</span>
          </button>
        </div>
      </nav>

      <div style={{ flex: 1, overflowY: 'auto' }}>
      <div style={s.content} className="gs-content">

        {/* ── Header ── */}
        <div className="gs-ap-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={s.shieldBox}><Shield size={20} /></div>
            <div>
              <h1 style={{ margin: 0, fontSize: '20px', fontWeight: '800', color: '#0f172a' }}>Transaction Report</h1>
              <p style={{ margin: 0, fontSize: '12px', color: '#64748b' }}>{totalActiveMembers} {totalActiveMembers === 1 ? 'Active Member' : 'Active Members'}</p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={downloadExcel} style={s.exportBtn}><FileDown size={16} /> Export {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}</button>
          </div>
        </div>

        {/* ── 3 Overall Totals (always all-time) ── */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '14px', marginBottom: '20px' }} className="gs-stats-3col">
          <div className="gs-ap-overall-0" style={{ background: 'linear-gradient(135deg, #C9A84C, #9A7A2C)', borderRadius: '14px', padding: '18px 22px', color: '#fff' }}>
            <p style={{ margin: '0 0 2px', fontSize: '11px', fontWeight: '700', opacity: 0.8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Overall Collected</p>
            <p style={{ margin: 0, fontSize: '24px', fontWeight: '800' }}>₹{overallTotalAmount.toLocaleString('en-IN')}</p>
            <p style={{ margin: '2px 0 0', fontSize: '11px', opacity: 0.7 }}>{data.length} Transactions · All Time</p>
          </div>
          <div className="gs-ap-overall-1" style={{ background: 'linear-gradient(135deg, #D97706, #92400e)', borderRadius: '14px', padding: '18px 22px', color: '#fff' }}>
            <p style={{ margin: '0 0 2px', fontSize: '11px', fontWeight: '700', opacity: 0.8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Total Gold Gram</p>
            <p style={{ margin: 0, fontSize: '24px', fontWeight: '800' }}>{overallGoldGram.toFixed(3)} g</p>
            <p style={{ margin: '2px 0 0', fontSize: '11px', opacity: 0.7 }}>All Time</p>
          </div>
          <div className="gs-ap-overall-2" style={{ background: 'linear-gradient(135deg, #475569, #1e293b)', borderRadius: '14px', padding: '18px 22px', color: '#fff' }}>
            <p style={{ margin: '0 0 2px', fontSize: '11px', fontWeight: '700', opacity: 0.8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Total Silver Gram</p>
            <p style={{ margin: 0, fontSize: '24px', fontWeight: '800' }}>{overallSilverGram.toFixed(3)} g</p>
            <p style={{ margin: '2px 0 0', fontSize: '11px', opacity: 0.7 }}>All Time</p>
          </div>
        </div>

        {/* ── Filter Toggle & Quick Filters ── */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
          <button
            onClick={() => setShowFilters(v => !v)}
            style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              padding: '8px 14px', borderRadius: '10px',
              border: `1.5px solid ${showFilters ? '#C9A84C' : '#e2e8f0'}`,
              background: showFilters ? '#fef9ec' : '#f8fafc',
              color: showFilters ? '#C9A84C' : '#64748b',
              fontSize: '13px', fontWeight: '700', cursor: 'pointer',
            }}
          >
            <Filter size={14} /> Filters
            {isAnyFilter && <span style={{ background: '#C9A84C', color: '#fff', borderRadius: '50%', width: '16px', height: '16px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: '800', marginLeft: '2px' }}>!</span>}
          </button>

          {isAnyFilter && (
            <button onClick={clearFilters} style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '7px 12px', borderRadius: '20px', border: '1px solid #fca5a5', background: '#fef2f2', color: '#ef4444', fontSize: '12px', fontWeight: '700', cursor: 'pointer' }}>
              <X size={12} /> Clear
            </button>
          )}
        </div>

        {/* ── Collapsible Filter Panel ── */}
        {showFilters && (
          <div style={{ background: '#fff', borderRadius: '14px', padding: '18px 20px', marginBottom: '16px', boxShadow: '0 1px 8px rgba(0,0,0,0.07)', display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'flex-end' }}>
            <div>
              <p style={{ margin: '0 0 6px', fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase' }}>Month</p>
              <MultiSelect options={MONTHS.map((m, i) => ({ value: i + 1, label: m }))} selected={selectedMonths} onChange={setSelectedMonths} placeholder="All Months" />
            </div>
            <div>
              <p style={{ margin: '0 0 6px', fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase' }}>Year</p>
              <MultiSelect options={availableYears.map(y => ({ value: y, label: String(y) }))} selected={selectedYears} onChange={setSelectedYears} placeholder="All Years" />
            </div>
            <div>
              <p style={{ margin: '0 0 6px', fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase' }}>From Date</p>
              <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} style={{ padding: '9px 12px', borderRadius: '10px', border: '1.5px solid #e2e8f0', fontSize: '13px', background: '#f8fafc', color: '#334155', outline: 'none' }} />
            </div>
            <div>
              <p style={{ margin: '0 0 6px', fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase' }}>To Date</p>
              <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} style={{ padding: '9px 12px', borderRadius: '10px', border: '1.5px solid #e2e8f0', fontSize: '13px', background: '#f8fafc', color: '#334155', outline: 'none' }} />
            </div>
          </div>
        )}

        {/* ── Filtered Period Stats (4 cards) ── */}
        <div className="gs-stats-4col" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '14px' }}>
          <div className="gs-ap-period-0" style={{ background: '#fff', borderRadius: '12px', padding: '16px 18px', boxShadow: '0 1px 6px rgba(0,0,0,0.06)', borderLeft: '4px solid #10b981' }}>
            <p style={{ margin: '0 0 2px', fontSize: '11px', fontWeight: '700', color: '#10b981', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Collected</p>
            <p style={{ margin: 0, fontSize: '20px', fontWeight: '800', color: '#0f172a' }}>₹{(goldCollectedAmt + silverCollectedAmt + moneyCollectedAmt).toLocaleString('en-IN')}</p>
            <p style={{ margin: '2px 0 0', fontSize: '11px', color: '#64748b' }}>{filteredData.length} {filteredData.length === 1 ? 'Transaction' : 'Transactions'} · {isAnyFilter ? 'filtered' : 'All Time'}</p>
          </div>
          <div className="gs-ap-period-1" style={{ background: '#fff', borderRadius: '12px', padding: '16px 18px', boxShadow: '0 1px 6px rgba(0,0,0,0.06)', borderLeft: '4px solid #ef4444' }}>
            <p style={{ margin: '0 0 2px', fontSize: '11px', fontWeight: '700', color: '#ef4444', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Closed</p>
            <p style={{ margin: 0, fontSize: '20px', fontWeight: '800', color: '#0f172a' }}>₹{(goldClosedAmt + silverClosedAmt + moneyClosedAmt).toLocaleString('en-IN')}</p>
            <p style={{ margin: '2px 0 0', fontSize: '11px', color: '#64748b' }}>{filteredClosed.length} {filteredClosed.length === 1 ? 'Entry' : 'Entries'} · {isAnyFilter ? 'filtered' : 'All Time'}</p>
          </div>
          <div className="gs-ap-period-2" style={{ background: '#fff', borderRadius: '12px', padding: '16px 18px', boxShadow: '0 1px 6px rgba(0,0,0,0.06)', borderLeft: '4px solid #6366f1' }}>
            <p style={{ margin: '0 0 2px', fontSize: '11px', fontWeight: '700', color: '#6366f1', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Members Joined</p>
            <p style={{ margin: 0, fontSize: '20px', fontWeight: '800', color: '#0f172a' }}>{filteredJoined.length}</p>
            <p style={{ margin: '2px 0 0', fontSize: '11px', color: '#64748b' }}>{isAnyFilter ? 'filtered period' : 'All Time'}</p>
          </div>
          <div className="gs-ap-period-3" style={{ background: '#fff', borderRadius: '12px', padding: '16px 18px', boxShadow: '0 1px 6px rgba(0,0,0,0.06)', borderLeft: '4px solid #f59e0b' }}>
            <p style={{ margin: '0 0 2px', fontSize: '11px', fontWeight: '700', color: '#f59e0b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Members Closed</p>
            <p style={{ margin: 0, fontSize: '20px', fontWeight: '800', color: '#0f172a' }}>{filteredRemoved.length}</p>
            <p style={{ margin: '2px 0 0', fontSize: '11px', color: '#64748b' }}>{isAnyFilter ? 'filtered period' : 'All Time'}</p>
          </div>
        </div>

        {/* ── Per-type Filtered Stats (6 cards) ── */}
        <div className="gs-stats-6col gs-ap-type-strip" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(155px, 1fr))', gap: '10px', marginBottom: '20px' }}>
          <div style={{ background: '#fffbeb', borderRadius: '12px', padding: '14px 16px', boxShadow: '0 1px 4px rgba(0,0,0,0.05)', borderTop: '3px solid #D97706' }}>
            <p style={{ margin: '0 0 1px', fontSize: '10px', fontWeight: '700', color: '#D97706', textTransform: 'uppercase' }}>Gold Collected</p>
            <p style={{ margin: '2px 0 0', fontSize: '16px', fontWeight: '800', color: '#0f172a' }}>₹{goldCollectedAmt.toLocaleString('en-IN')}</p>
            <p style={{ margin: '2px 0 0', fontSize: '11px', color: '#D97706', fontWeight: '600' }}>{goldCollectedGram.toFixed(3)} g</p>
          </div>
          <div style={{ background: '#fef2f2', borderRadius: '12px', padding: '14px 16px', boxShadow: '0 1px 4px rgba(0,0,0,0.05)', borderTop: '3px solid #ef4444' }}>
            <p style={{ margin: '0 0 1px', fontSize: '10px', fontWeight: '700', color: '#ef4444', textTransform: 'uppercase' }}>Gold Closed</p>
            <p style={{ margin: '2px 0 0', fontSize: '16px', fontWeight: '800', color: '#0f172a' }}>₹{goldClosedAmt.toLocaleString('en-IN')}</p>
            <p style={{ margin: '2px 0 0', fontSize: '11px', color: '#ef4444', fontWeight: '600' }}>{goldClosedGram.toFixed(3)} g</p>
          </div>
          <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '14px 16px', boxShadow: '0 1px 4px rgba(0,0,0,0.05)', borderTop: '3px solid #64748b' }}>
            <p style={{ margin: '0 0 1px', fontSize: '10px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase' }}>Silver Collected</p>
            <p style={{ margin: '2px 0 0', fontSize: '16px', fontWeight: '800', color: '#0f172a' }}>₹{silverCollectedAmt.toLocaleString('en-IN')}</p>
            <p style={{ margin: '2px 0 0', fontSize: '11px', color: '#64748b', fontWeight: '600' }}>{silverCollectedGram.toFixed(3)} g</p>
          </div>
          <div style={{ background: '#fef2f2', borderRadius: '12px', padding: '14px 16px', boxShadow: '0 1px 4px rgba(0,0,0,0.05)', borderTop: '3px solid #ef4444' }}>
            <p style={{ margin: '0 0 1px', fontSize: '10px', fontWeight: '700', color: '#ef4444', textTransform: 'uppercase' }}>Silver Closed</p>
            <p style={{ margin: '2px 0 0', fontSize: '16px', fontWeight: '800', color: '#0f172a' }}>₹{silverClosedAmt.toLocaleString('en-IN')}</p>
            <p style={{ margin: '2px 0 0', fontSize: '11px', color: '#ef4444', fontWeight: '600' }}>{silverClosedGram.toFixed(3)} g</p>
          </div>
          <div style={{ background: '#f0fdf4', borderRadius: '12px', padding: '14px 16px', boxShadow: '0 1px 4px rgba(0,0,0,0.05)', borderTop: '3px solid #16a34a' }}>
            <p style={{ margin: '0 0 1px', fontSize: '10px', fontWeight: '700', color: '#16a34a', textTransform: 'uppercase' }}>Money Collected</p>
            <p style={{ margin: '2px 0 0', fontSize: '16px', fontWeight: '800', color: '#0f172a' }}>₹{moneyCollectedAmt.toLocaleString('en-IN')}</p>
            <p style={{ margin: '2px 0 0', fontSize: '11px', color: '#64748b', fontWeight: '600' }}>{moneyCollected.length} Transactions</p>
          </div>
          <div style={{ background: '#fef2f2', borderRadius: '12px', padding: '14px 16px', boxShadow: '0 1px 4px rgba(0,0,0,0.05)', borderTop: '3px solid #ef4444' }}>
            <p style={{ margin: '0 0 1px', fontSize: '10px', fontWeight: '700', color: '#ef4444', textTransform: 'uppercase' }}>Money Closed</p>
            <p style={{ margin: '2px 0 0', fontSize: '16px', fontWeight: '800', color: '#0f172a' }}>₹{moneyClosedAmt.toLocaleString('en-IN')}</p>
            <p style={{ margin: '2px 0 0', fontSize: '11px', color: '#64748b', fontWeight: '600' }}>{moneyClosed.length} Transactions</p>
          </div>
        </div>

        {/* ── Tab Bar ── */}
        <div style={{ display: 'flex', gap: '6px', marginBottom: '12px', flexWrap: 'wrap' }}>
          {[
            { key: 'members', label: `👤 Members (${membersTabRows.length})` },
            { key: 'gold',    label: `🥇 Gold (${goldCollected.length})` },
            { key: 'silver',  label: `🥈 Silver (${silverCollected.length})` },
            { key: 'money',   label: `💰 Money (${moneyCollected.length})` },
            { key: 'closed',  label: `🗑 Closed (${filteredClosed.length})` },
          ].map(tab => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)} style={{
              padding: '8px 16px', borderRadius: '20px', border: 'none',
              fontSize: '13px', fontWeight: '700', cursor: 'pointer',
              background: activeTab === tab.key ? 'linear-gradient(135deg, #C9A84C, #9A7A2C)' : '#f1f5f9',
              color: activeTab === tab.key ? '#fff' : '#64748b',
              boxShadow: activeTab === tab.key ? '0 4px 10px rgba(201,168,76,0.3)' : 'none',
            }}>{tab.label}</button>
          ))}
        </div>

        {/* ── Table ── */}
        <div style={s.tableCard} className="gs-ap-table">
          <div className="gs-table-scroll" style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: '480px' }}>
            {tableData.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '60px 20px', color: '#94a3b8', fontSize: '15px' }}>
                <div style={{ fontSize: '36px', marginBottom: '12px' }}>📭</div>
                No records found{isAnyFilter ? ' for this filter.' : '.'}
              </div>
            ) : activeTab === 'members' ? (
              <table style={s.table}>
                <thead>
                  <tr>
                    <th style={{ ...s.th, textAlign: 'center' }}>Status</th>
                    <th style={s.th}>Customer ID</th>
                    <th style={s.th}>Name</th>
                    <th style={{ ...s.th, textAlign: 'center' }}>Type</th>
                    <th style={{ ...s.th, textAlign: 'center' }}>Phone</th>
                    <th style={{ ...s.th, textAlign: 'center' }}>Date &amp; Time</th>
                    <th style={s.th}>By</th>
                  </tr>
                </thead>
                <tbody>
                  {tableData.map((d, i) => (
                    <tr key={d._type + String(d.id) + i} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                      <td style={{ ...s.td, textAlign: 'center' }}>
                        <span style={{ display: 'inline-block', borderRadius: '20px', padding: '2px 10px', fontSize: '12px', fontWeight: '700', background: d._type === 'joined' ? '#dcfce7' : '#fef2f2', color: d._type === 'joined' ? '#16a34a' : '#ef4444' }}>
                          {d._type === 'joined' ? 'Joined' : 'Removed'}
                        </span>
                      </td>
                      <td style={s.td}><span style={s.badge}>{d.id || '—'}</span></td>
                      <td style={{ ...s.td, fontWeight: '700', color: '#0f172a' }}>{d.name || '—'}</td>
                      <td style={{ ...s.td, textAlign: 'center' }}>
                        {d.saving_type === 'gold' ? <span style={s.badge}>Gold</span>
                          : d.saving_type === 'silver' ? <span style={{ ...s.badge, background: '#f1f5f9', color: '#475569' }}>Silver</span>
                          : <span style={{ ...s.badge, background: '#dcfce7', color: '#16a34a' }}>Money</span>}
                      </td>
                      <td style={{ ...s.td, textAlign: 'center' }}>{d.phone || '—'}</td>
                      <td style={{ ...s.td, textAlign: 'center', color: '#64748b', whiteSpace: 'nowrap' }}>{d.date ? new Date(d.date).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }) : '—'}</td>
                      <td style={{ ...s.td, color: '#64748b', fontSize: '12px' }}>{d.extra || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : activeTab === 'closed' ? (
              <table style={s.table}>
                <thead>
                  <tr>
                    <th style={{ ...s.th, textAlign: 'center' }}>Member</th>
                    <th style={{ ...s.th, textAlign: 'center' }}>Customer ID</th>
                    <th style={{ ...s.th, textAlign: 'center' }}>Type</th>
                    <th style={{ ...s.th, textAlign: 'center' }}>Gram</th>
                    <th style={{ ...s.th, textAlign: 'center' }}>Amount</th>
                    <th style={{ ...s.th, textAlign: 'center' }}>Transaction Date &amp; Time</th>
                    <th style={{ ...s.th, textAlign: 'center' }}>Note</th>
                    <th style={{ ...s.th, textAlign: 'center' }}>Deleted By</th>
                    <th style={{ ...s.th, textAlign: 'center' }}>Deleted At</th>
                  </tr>
                </thead>
                <tbody>
                  {tableData.map((d, i) => (
                    <tr key={d._key || i} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                      <td style={{ ...s.td, textAlign: 'center', fontWeight: '700', color: '#0f172a' }}>{d.member_name || '—'}</td>
                      <td style={{ ...s.td, textAlign: 'center', color: '#94a3b8', fontWeight: '600' }}>#{d.user_id || '—'}</td>
                      <td style={{ ...s.td, textAlign: 'center' }}>
                        {d.saving_type === 'gold' ? <span style={s.badge}>Gold</span>
                          : d.saving_type === 'silver' ? <span style={{ ...s.badge, background: '#f1f5f9', color: '#475569' }}>Silver</span>
                          : <span style={{ ...s.badge, background: '#dcfce7', color: '#16a34a' }}>Money</span>}
                      </td>
                      <td style={{ ...s.td, textAlign: 'center', fontWeight: '700', color: '#D97706' }}>
                        {d.gram ? Number(d.gram).toFixed(3) + ' g' : '—'}
                      </td>
                      <td style={{ ...s.td, textAlign: 'center' }}>
                        <span style={{ ...s.amtBadge, background: '#fef2f2', color: '#ef4444' }}>₹{Number(d.amount || 0).toLocaleString('en-IN')}</span>
                      </td>
                      <td style={{ ...s.td, textAlign: 'center', color: '#64748b', whiteSpace: 'nowrap' }}>
                        {d.created_at
                          ? new Date(d.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true })
                          : d.date ? new Date(d.date + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                      </td>
                      <td style={{ ...s.td, textAlign: 'center', color: '#64748b', fontStyle: 'italic', maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis' }}>{d.note || '—'}</td>
                      <td style={{ ...s.td, textAlign: 'center', maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis' }} title={d.deleted_by || '—'}>{d.deleted_by || '—'}</td>
                      <td style={{ ...s.td, textAlign: 'center', color: '#64748b', whiteSpace: 'nowrap' }}>
                        {d.deleted_at ? new Date(d.deleted_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <table style={s.table}>
                <thead>
                  <tr>
                    <th style={s.th}>Customer ID</th>
                    <th style={s.th}>Member</th>
                    {activeTab !== 'money' && <th style={{ ...s.th, textAlign: 'center' }}>Gram</th>}
                    <th style={{ ...s.th, textAlign: 'center' }}>Amount</th>
                    <th style={{ ...s.th, textAlign: 'center' }}>Date &amp; Time</th>
                    <th style={s.th}>Note</th>
                    <th style={s.th}>Added By</th>
                  </tr>
                </thead>
                <tbody>
                  {tableData.map((d, i) => (
                    <tr key={d.id || i} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                      <td style={s.td}><span style={s.badge}>{d.user_id || '—'}</span></td>
                      <td style={{ ...s.td, fontWeight: '700', color: '#0f172a' }}>{d.users?.name || '—'}</td>
                      {activeTab !== 'money' && (
                        <td style={{ ...s.td, textAlign: 'center', fontWeight: '700', color: activeTab === 'gold' ? '#D97706' : '#475569' }}>
                          {d.gram ? Number(d.gram).toFixed(3) + ' g' : '—'}
                        </td>
                      )}
                      <td style={{ ...s.td, textAlign: 'center' }}>
                        <span style={s.amtBadge}>₹{Number(d.amount || 0).toLocaleString('en-IN')}</span>
                      </td>
                      <td style={{ ...s.td, textAlign: 'center', color: '#64748b', whiteSpace: 'nowrap' }}>
                        {d.created_at
                          ? new Date(d.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true })
                          : d.date ? new Date(d.date + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                      </td>
                      <td style={{ ...s.td, color: '#64748b', fontStyle: 'italic', maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis' }}>{d.note || '—'}</td>
                      <td style={{ ...s.td, maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis' }} title={d.added_by || '—'}>{d.added_by || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          <div style={{ padding: '12px 20px', background: '#f8fafc', borderTop: '1px solid #f1f5f9', fontSize: '12px', color: '#94a3b8', display: 'flex', justifyContent: 'space-between' }}>
            <span>{tableData.length} {tableData.length === 1 ? 'Record shown' : 'Records shown'}</span>
            {isAnyFilter && <span style={{ color: '#C9A84C', fontWeight: '700' }}>⚡ Filters applied</span>}
          </div>
        </div>

      </div>
      </div>
    </div>
  );
}
